import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { APP_NAME, APP_TAGLINE, COPYRIGHT } from '@/lib/branding';
import LumoLogo from '@/assets/lumo-logo.svg';
import LumoLogoDark from '@/assets/lumo-logo-dark.svg';
import { supabase } from '@/integrations/supabase/client';
import { Eye, EyeOff, AlertCircle } from 'lucide-react';
import { SITE_URL } from '@/lib/appConfig';
import { useTheme } from 'next-themes';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { logger } from '@/utils/logger';

const signInSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
});

const signUpSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
  firstName: z.string().min(1, 'Nome é obrigatório'),
  lastName: z.string().min(1, 'Sobrenome é obrigatório'),
});

type SignInFormData = z.infer<typeof signInSchema>;
type SignUpFormData = z.infer<typeof signUpSchema>;

const Auth = () => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showPasswordSignIn, setShowPasswordSignIn] = useState(false);
  const [showPasswordSignUp, setShowPasswordSignUp] = useState(false);
  const [isRecovery, setIsRecovery] = useState(false);
  const { user, signIn, signUp } = useAuth();
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();
  
  const [authError, setAuthError] = useState<string | null>(null);

  const signInForm = useForm<SignInFormData>({
    resolver: zodResolver(signInSchema),
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onChange',
  });

  const signUpForm = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      email: '',
      password: '',
      firstName: '',
      lastName: '',
    },
    mode: 'onChange',
  });

  // Reset forms when switching between sign in and sign up
  useEffect(() => {
    signInForm.reset();
    signUpForm.reset();
    setAuthError(null);
  }, [isSignUp, signInForm, signUpForm]);

  // Redirect if already authenticated
  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  // Detect Supabase recovery flow and show reset form
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setIsRecovery(true);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Handle possible error returned by Supabase redirect
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const type = searchParams.get('type') || hashParams.get('type');
    const accessToken = hashParams.get('access_token') || searchParams.get('access_token');
    const error = searchParams.get('error') || hashParams.get('error');
    const errorCode = searchParams.get('error_code') || hashParams.get('error_code');
    const errorDescription = searchParams.get('error_description') || hashParams.get('error_description');
    
    // Only show recovery form if explicitly requested via recovery flow
    if (type === 'recovery') {
      setIsRecovery(true);
    }
    
    if (error) {
      const msg = errorDescription?.replace(/\+/g, ' ') || (errorCode === 'otp_expired' ? 'Link de recuperação inválido ou expirado. Solicite um novo link.' : 'Ocorreu um erro ao validar o link de recuperação.');
      toast.error(msg);
    }
  }, []);

  const resetSchema = z.object({
    newPassword: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
    confirmPassword: z.string().min(6, 'Senha deve ter pelo menos 6 caracteres'),
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: 'As senhas não coincidem',
    path: ['confirmPassword'],
  });

  type ResetFormData = z.infer<typeof resetSchema>;
  const resetForm = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
    defaultValues: { newPassword: '', confirmPassword: '' },
    mode: 'onChange',
  });

  const handleResetPassword = async (data: ResetFormData) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: data.newPassword });
      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Senha atualizada com sucesso. Faça login novamente.');
        setIsRecovery(false);
        navigate('/auth');
      }
    } catch (_e) {
      toast.error('Erro ao atualizar senha');
    } finally {
      setLoading(false);
    }
  };
  const handleSignIn = async (data: SignInFormData) => {
    setLoading(true);
    setAuthError(null);
    logger.info('Tentativa de login iniciada', { email: data.email });
    
    try {
      const normalizedEmail = data.email.trim().toLowerCase();
      const { error } = await signIn(normalizedEmail, data.password);
      if (error) {
        logger.warn('Falha no login', { email: normalizedEmail, error: error.message });
        console.error('Login error:', error);
        if (error.message === 'Invalid login credentials') {
          setAuthError('Email ou senha incorretos.');
        } else {
          setAuthError(error.message);
        }
        toast.error('Falha na autenticação');
      } else {
        logger.info('Login realizado com sucesso', { email: normalizedEmail });
        toast.success('Login realizado com sucesso');
      }
    } catch (error) {
      logger.error('Erro inesperado no login', error, { email: data.email });
      setAuthError('Erro inesperado ao fazer login.');
      toast.error('Erro ao fazer login');
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (data: SignUpFormData) => {
    setLoading(true);
    logger.info('Tentativa de cadastro iniciada', { email: data.email });

    try {
      const finalData = {
        email: data.email.trim().toLowerCase(),
        password: data.password,
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim()
      };
      const { error } = await signUp(finalData.email, finalData.password, finalData.firstName, finalData.lastName);
      if (error) {
        logger.warn('Falha no cadastro', { email: finalData.email, error: error.message });
        toast.error(error.message);
      } else {
        logger.info('Cadastro realizado com sucesso', { email: finalData.email });
        toast.success('Conta criada com sucesso! Verifique seu email.');
      }
    } catch (error) {
      logger.error('Erro inesperado no cadastro', error, { email: data.email });
      toast.error('Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    const email = (signInForm.getValues('email') || '').trim().toLowerCase();
    if (!email) {
      toast.error('Informe seu email para recuperar a senha');
      return;
    }
    setResetLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${SITE_URL}/auth/reset`
      });
      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Enviamos um link de recuperação para seu email');
      }
    } catch (_e) {
      toast.error('Erro ao solicitar recuperação de senha');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 bg-gradient-to-b from-background to-muted/40">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center space-y-2">
          <img 
            src={resolvedTheme === 'dark' ? LumoLogoDark : LumoLogo} 
            alt={APP_NAME} 
            className="mx-auto h-12 w-auto" 
          />
          <h1 className="text-4xl font-logo font-bold tracking-tight text-foreground">{APP_NAME}</h1>
          <p className="text-muted-foreground">{APP_TAGLINE}</p>
        </div>
        <Card className="w-full max-w-md border-muted/40 shadow-lg">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-2xl font-bold">
              {isSignUp ? 'Criar Conta' : 'Entrar'}
            </CardTitle>
            <CardDescription className="text-base">
              {isSignUp 
                ? 'Preencha os dados para criar sua conta'
                : 'Entre com suas credenciais'
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isSignUp ? (
            <Form {...signUpForm}>
              <form key="signup-form" onSubmit={signUpForm.handleSubmit(handleSignUp)} className="space-y-4">
                <FormField
                  control={signUpForm.control}
                  name="firstName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome</FormLabel>
                      <FormControl>
                        <Input placeholder="Seu nome" {...field} disabled={loading} aria-label="Nome" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={signUpForm.control}
                  name="lastName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sobrenome</FormLabel>
                      <FormControl>
                        <Input placeholder="Seu sobrenome" {...field} disabled={loading} aria-label="Sobrenome" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={signUpForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="seu@email.com" {...field} disabled={loading} aria-label="Email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={signUpForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Senha</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input type={showPasswordSignUp ? 'text' : 'password'} placeholder="Sua senha" {...field} disabled={loading} aria-label="Senha" />
                          <button
                            type="button"
                            onClick={() => setShowPasswordSignUp((v) => !v)}
                            className="absolute inset-y-0 right-2 flex items-center text-muted-foreground"
                            aria-label={showPasswordSignUp ? 'Ocultar' : 'Mostrar'}
                          >
                            {showPasswordSignUp ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 transition-colors" disabled={loading}>
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></div>
                      Criando conta...
                    </div>
                  ) : 'Criar Conta'}
                </Button>
              </form>
            </Form>
          ) : (
            isRecovery ? (
              <Form {...resetForm}>
                <form key="reset-form" onSubmit={resetForm.handleSubmit(handleResetPassword)} className="space-y-4">
                  <FormField
                    control={resetForm.control}
                    name="newPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Nova Senha</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="Nova senha" {...field} disabled={loading} aria-label="Nova senha" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={resetForm.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirmar Senha</FormLabel>
                        <FormControl>
                          <Input type="password" placeholder="Confirme a senha" {...field} disabled={loading} aria-label="Confirme a senha" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <Button type="submit" className="w-full bg-primary hover:bg-primary/90 transition-colors" disabled={loading}>
                    {loading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></div>
                        Atualizando...
                      </div>
                    ) : 'Atualizar Senha'}
                  </Button>
                </form>
              </Form>
            ) : (
            <Form {...signInForm}>
              <form key="signin-form" onSubmit={signInForm.handleSubmit(handleSignIn)} className="space-y-4">
                {authError && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Erro</AlertTitle>
                    <AlertDescription>{authError}</AlertDescription>
                  </Alert>
                )}
                <FormField
                  control={signInForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input type="email" placeholder="seu@email.com" {...field} disabled={loading} aria-label="Email" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={signInForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Senha</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input type={showPasswordSignIn ? 'text' : 'password'} placeholder="Sua senha" {...field} disabled={loading} aria-label="Senha" />
                          <button
                            type="button"
                            onClick={() => setShowPasswordSignIn((v) => !v)}
                            className="absolute inset-y-0 right-2 flex items-center text-muted-foreground"
                            aria-label={showPasswordSignIn ? 'Ocultar' : 'Mostrar'}
                          >
                            {showPasswordSignIn ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex items-center justify-between">
                  <Button type="button" variant="link" onClick={handleForgotPassword} disabled={resetLoading || loading} className="px-0">
                    Esqueci minha senha
                  </Button>
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 transition-colors" disabled={loading}>
                  {loading ? (
                    <div className="flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></div>
                      Entrando...
                    </div>
                  ) : 'Entrar'}
                </Button>
              </form>
            </Form>
            )
          )}
          
          <div className="mt-6 text-center">
            <Button
              variant="link"
              onClick={() => {
                setIsSignUp(!isSignUp);
              }}
              className="text-sm font-medium"
            >
              {isSignUp 
                ? 'Já tem conta? Faça login' 
                : 'Não tem conta? Cadastre-se'
              }
            </Button>
          </div>
          
          <div className="mt-4 pt-4 border-t border-border/40 text-center">
            <p className="text-xs text-muted-foreground">
              {COPYRIGHT}. Todos os direitos reservados.
            </p>
          </div>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default Auth;
